import React, { useCallback, useEffect, useState } from 'react';
import { clubService } from '../services';
import { useDialog } from '../context/DialogContext';

/**
 * FORUM CATEGORIES — club management. Every club starts with a default set
 * (General · Events · Introductions · Help · Off-topic) seeded the first time the
 * forum is opened; this is where an owner or admin renames, reorders, deletes, or
 * adds their own.
 * docs/plans/2026-08-club-forum/PLAN.md
 */
export default function ForumCategoriesPanel({ clubId, enabled = true }) {
  const { showConfirm, showAlert } = useDialog();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await clubService.listForumCategories(clubId);
    if (r.success) setCategories(r.data || []);
    setLoading(false);
  }, [clubId]);

  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  const reset = () => { setEditingId(null); setName(''); setDescription(''); };

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const payload = { name: name.trim(), description: description.trim() || null };
    const r = editingId
      ? await clubService.updateForumCategory(clubId, editingId, payload)
      : await clubService.createForumCategory(clubId, payload);
    setBusy(false);
    if (r.success) { reset(); load(); }
    else showAlert({ title: 'COULD NOT SAVE', message: r.error || 'Try again.', type: 'error' });
  };

  const edit = (c) => { setEditingId(c.id); setName(c.name); setDescription(c.description || ''); };

  const move = async (c, delta) => {
    const ordered = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = ordered.findIndex((x) => x.id === c.id);
    const target = ordered[index + delta];
    if (!target) return;
    // Swap the two sort values; the list re-sorts on reload.
    await clubService.updateForumCategory(clubId, c.id, { name: c.name, description: c.description, sortOrder: target.sortOrder });
    await clubService.updateForumCategory(clubId, target.id, { name: target.name, description: target.description, sortOrder: c.sortOrder });
    load();
  };

  const remove = async (c) => {
    const ok = await showConfirm({
      title: 'DELETE CATEGORY',
      message: c.threadCount > 0
        ? `${c.threadCount} thread${c.threadCount === 1 ? '' : 's'} will stay, just uncategorized.`
        : 'Delete this category?',
      confirmLabel: 'DELETE',
      variant: 'danger',
    });
    if (!ok) return;
    const r = await clubService.deleteForumCategory(c.id);
    if (r.success) { if (editingId === c.id) reset(); load(); }
    else showAlert({ title: 'COULD NOT DELETE', message: r.error || 'Try again.', type: 'error' });
  };

  if (!enabled) {
    return (
      <div id="forum-categories" className="border-2 border-black p-4">
        <div className="font-mono text-xs font-bold uppercase tracking-wider text-black">FORUM CATEGORIES</div>
        <p className="mt-2 font-mono text-[11px] text-gray-500">
          Turn the forum on above to set up categories.
        </p>
      </div>
    );
  }

  return (
    <div id="forum-categories" className="border-2 border-black p-4">
      <div className="font-mono text-xs font-bold uppercase tracking-wider text-black">FORUM CATEGORIES</div>
      <p className="mt-1 font-mono text-[11px] text-gray-500">
        How threads get filed. Members pick one when they post.
      </p>

      {loading ? (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">Loading…</p>
      ) : (
        <div className="mt-3 border-2 border-black">
          {categories.length === 0 && (
            <p className="p-4 font-mono text-[11px] text-gray-500">No categories yet — add one below.</p>
          )}
          {categories.map((c, i) => (
            <div key={c.id} className="flex items-center gap-3 border-b-2 border-black px-3 py-2.5 last:border-b-0">
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => move(c, -1)}
                  disabled={i === 0}
                  aria-label={`Move ${c.name} up`}
                  className="px-1 font-mono text-[10px] leading-none text-gray-400 hover:text-black disabled:opacity-30"
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => move(c, 1)}
                  disabled={i === categories.length - 1}
                  aria-label={`Move ${c.name} down`}
                  className="px-1 font-mono text-[10px] leading-none text-gray-400 hover:text-black disabled:opacity-30"
                >
                  ▼
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-mono text-sm font-bold uppercase tracking-wide">{c.name}</span>
                  {c.isDefault && (
                    <span className="border border-gray-300 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-widest text-gray-400">
                      Default
                    </span>
                  )}
                </div>
                {c.description && <div className="truncate font-mono text-[11px] text-gray-500">{c.description}</div>}
              </div>
              <span className="flex-shrink-0 font-mono text-[10px] uppercase tracking-wide text-gray-400">
                {c.threadCount} {c.threadCount === 1 ? 'thread' : 'threads'}
              </span>
              <button
                type="button"
                onClick={() => edit(c)}
                className="flex-shrink-0 border-2 border-black px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide hover:bg-black hover:text-white"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => remove(c)}
                className="flex-shrink-0 border-2 border-red-600 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide text-red-600 hover:bg-red-600 hover:text-white"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 space-y-2">
        <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500">
          {editingId ? 'Edit category' : 'Add a category'}
        </div>
        <input
          type="text"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (e.g. Boat days)"
          className="w-full border-2 border-black px-3 py-2 font-mono text-sm outline-none"
        />
        <input
          type="text"
          value={description}
          maxLength={160}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What goes here (optional)"
          className="w-full border-2 border-black px-3 py-2 font-mono text-sm outline-none"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={busy || !name.trim()}
            className="border-2 border-black bg-black px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
          >
            {busy ? 'Saving…' : (editingId ? 'Save' : 'Add')}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={reset}
              className="border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
